import React, { useEffect, useMemo, useState } from "react";
import api, { downloadFile } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import ActionButton from "../components/ActionButton";
import ReportGallery from "../components/ReportGallery";
import SimpleReportBuilder from "../components/SimpleReportBuilder";
import type { BuilderDraft } from "../components/SimpleReportBuilder";
import { formatDateTime } from "../utils/datetime";

/* Custom Report Builder.
 *
 * Three sub-views:
 *   - "list" — saved reports, with Run/CSV/Edit/Delete actions
 *   - "edit" — builder form (pick type → pick columns → add filters → save)
 *   - "preview" — table of result rows, with CSV/PDF download and a
 *     "Schedule…" button that opens the schedule modal
 *
 * State lives in this single component; navigating away and back loses
 * the builder draft. That's intentional for v1 — saving early & often
 * is what we want users to do.
 */

type ColumnDef = {
  key: string;
  label: string;
  type: "string" | "int" | "float" | "bool" | "datetime" | "enum";
  enum_choices: string[] | null;
  filterable: boolean;
};

type TypeDef = {
  key: string;
  label: string;
  description: string;
  /** Picker grouping. Seventeen report types in one flat dropdown is a list to
   *  read, not a menu to choose from. */
  category: string;
  portal_safe: boolean;
  /** A sensible starting column set, so choosing a type gives you a runnable
   *  report immediately instead of an empty form. */
  default_columns: string[];
  columns: ColumnDef[];
  ops_by_type: Record<string, string[]>;
};

/** A generated document (PDF/CSV/zip) rather than a row grid. Same gallery,
 *  different action: there is nothing to preview, so the card downloads. */
type DocReport = {
  key: string;
  label: string;
  description: string;
  category: string;
  needs_customer: boolean;
  formats: string[];
  params: Record<string, { label: string; choices?: any[]; required?: boolean }>;
};

type Filter = { field: string; op: string; value: any };
type OrderBy = { field: string; direction: "asc" | "desc" };

type SavedReport = {
  id: number;
  name: string;
  description: string | null;
  report_type: string;
  columns: string[];
  filters: Filter[];
  order_by: OrderBy | null;
  row_limit: number;
  built_in?: boolean;
  builtin_key?: string | null;
  created_by_username: string | null;
  created_at: string;
  updated_at: string;
};

type PreviewResult = {
  columns: string[];
  rows: Record<string, any>[];
  total_rows: number;
  capped: boolean;
  row_limit: number;
};

type Schedule = {
  id: number;
  saved_report_id: number;
  cadence: "daily" | "weekly" | "monthly";
  hour_utc: number;
  day_of_week: number | null;
  day_of_month: number | null;
  recipients: string[];
  output_format: "csv" | "pdf";
  enabled: boolean;
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_error: string | null;
  next_run_at: string | null;
};

export default function ReportsBuilder() {
  const { user } = useAuth();
  const { currentCustomerId } = useCustomer();
  // Scope preview/run/download to the globally selected customer. GETs get this
  // via the axios interceptor; the two POSTs below must pass it explicitly.
  const scopeCfg =
    currentCustomerId != null ? { params: { customer_id: currentCustomerId } } : undefined;
  const isAdmin = user?.role === "admin";

  const [types, setTypes] = useState<TypeDef[]>([]);
  const [saved, setSaved] = useState<SavedReport[]>([]);
  const [view, setView] = useState<"list" | "edit" | "preview">("list");
  /** Shared by the gallery and the saved-reports table below it. */
  const [gallerySearch, setGallerySearch] = useState("");
  const [docReports, setDocReports] = useState<DocReport[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Builder draft
  const [draftName, setDraftName] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [draftType, setDraftType] = useState("");
  const [draftColumns, setDraftColumns] = useState<string[]>([]);
  const [draftFilters, setDraftFilters] = useState<Filter[]>([]);
  const [draftOrderBy, setDraftOrderBy] = useState<OrderBy | null>(null);
  const [draftLimit, setDraftLimit] = useState(1000);

  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>("");

  // Schedule modal state
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [schedDraft, setSchedDraft] = useState<Partial<Schedule> & { recipientsText?: string }>({
    cadence: "weekly",
    hour_utc: 13,
    day_of_week: 1,
    output_format: "csv",
    enabled: true,
    recipientsText: "",
  });
  const [schedEditingId, setSchedEditingId] = useState<number | null>(null);

  useEffect(() => {
    api.get("/api/v1/reports-builder/types").then((r) => setTypes(r.data));
    loadSaved();
  }, []);

  // Document reports are a separate registry (services/document_reports.py):
  // generated PDFs rather than row grids. Failing to load them must not take
  // the gallery down — the tabular reports are the common case.
  useEffect(() => {
    api
      .get("/api/v1/reports-builder/document-reports")
      .then((r) => setDocReports(r.data || []))
      .catch(() => setDocReports([]));
  }, []);

  const loadSaved = () =>
    api.get("/api/v1/reports-builder").then((r) => setSaved(r.data));

  const selectedType = useMemo(
    () => types.find((t) => t.key === draftType) || null,
    [types, draftType],
  );

  /** Types bucketed by category, categories in a fixed order.
   *  Alphabetical would put "Devices" before "Operations" before "Service
   *  delivery" purely by accident; this order is the one a technician thinks
   *  in. Anything with a category not listed here still shows, at the end. */

  /** Saved reports matching the gallery search. Same box, both lists. */
  const filteredSaved = useMemo(() => {
    const needle = gallerySearch.trim().toLowerCase();
    if (!needle) return saved;
    return saved.filter((sr) => {
      const hay = [sr.name, sr.description || "", sr.report_type, sr.created_by_username || ""]
        .join(" ")
        .toLowerCase();
      return needle.split(/\s+/).every((w) => hay.includes(w));
    });
  }, [saved, gallerySearch]);

  /** Human label for a column key, falling back to the key if the type hasn't
   *  loaded yet (a hard refresh on the preview view). */
  const columnLabel = (key: string) =>
    selectedType?.columns.find((c) => c.key === key)?.label || key;

  /** A preview cell, formatted the way the exports already are.
   *
   *  The preview rendered `String(value)` for everything, so every datetime
   *  in every report arrived as "2026-09-21T11:08:42.665235+00:00". CSV and
   *  PDF were made friendly ("21 Sep 2026, 11:08 am") but the screen people
   *  actually read was missed — friendly dates were meant to be across the
   *  board, and this is the most-looked-at surface of the lot.
   *
   *  Driven off the column's declared TYPE rather than by sniffing the
   *  value: a serial number or an asset tag can look like a date, and
   *  silently reinterpreting one would be worse than the raw string. */
  const renderCell = (key: string, value: unknown) => {
    if (value === null || value === undefined || value === "") return null;
    const type = selectedType?.columns.find((c) => c.key === key)?.type;
    if (type === "datetime") return formatDateTime(String(value));
    if (type === "bool") return value ? "Yes" : "No";
    return String(value);
  };

  /** Download a standard report straight from the gallery.

   *  Getting a CSV of AD users used to be run → wait → download. The ad-hoc
   *  endpoint already existed; the gallery just could not reach it. */
  const quickDownload = async (t: TypeDef, fmt: "csv" | "pdf") => {
    setBusy(true);
    setMsg("");
    try {
      const columns = t.default_columns?.length
        ? t.default_columns
        : t.columns.slice(0, 6).map((c) => c.key);
      await downloadFile(
        `/api/v1/reports-builder/download.${fmt}`,
        `${t.label.replace(/[^A-Za-z0-9._-]+/g, "_")}.${fmt}`,
        scopeCfg?.params,
        {
          method: "post",
          data: {
            report_type: t.key,
            columns,
            filters: [],
            order_by: null,
            row_limit: 5000,
          },
        },
      );
    } catch (e: any) {
      setMsg(e?.message || "Could not prepare that download");
    } finally {
      setBusy(false);
    }
  };

  /** Run a document report from the gallery.
   *
   *  Honours the global customer selector: a document that needs a customer
   *  uses the one currently selected, and says so rather than failing with a
   *  400 if none is. */
  const runDocument = async (d: DocReport, fmt: string) => {
    setMsg("");
    const cid = currentCustomerId != null ? Number(currentCustomerId) : null;
    if (d.needs_customer && !cid) {
      setMsg(`${d.label} is per-customer — pick a customer at the top of the page first.`);
      return;
    }
    const qs = new URLSearchParams({ fmt });
    if (cid) qs.set("customer_id", String(cid));
    // The framework report needs one naming a framework; default to the first
    // available rather than making the gallery a form.
    if (d.key === "compliance_framework") {
      const choices = d.params?.framework?.choices || [];
      if (!choices.length) {
        setMsg("No compliance frameworks are configured.");
        return;
      }
      qs.set("framework", String((choices[0] as any).value));
    }
    setBusy(true);
    try {
      await downloadFile(
        `/api/v1/reports-builder/document-reports/${d.key}/download?${qs.toString()}`,
        `${d.label.replace(/[^A-Za-z0-9._-]+/g, "_")}.${fmt}`,
      );
    } catch (e: any) {
      setMsg(e?.message || "Could not generate that document");
    } finally {
      setBusy(false);
    }
  };

  /** Download whatever is currently on screen, saved or not. */
  const downloadAdhoc = async (fmt: "csv" | "pdf") => {
    if (!draftType || draftColumns.length === 0) return;
    setMsg("");
    try {
      await downloadFile(
        `/api/v1/reports-builder/download.${fmt}`,
        `${(draftName || selectedType?.label || "report").replace(/[^A-Za-z0-9._-]+/g, "_")}.${fmt}`,
        scopeCfg?.params,
        {
          method: "post",
          data: {
            report_type: draftType,
            columns: draftColumns,
            filters: draftFilters,
            order_by: draftOrderBy,
            row_limit: draftLimit,
          },
        },
      );
    } catch (e: any) {
      setMsg(e?.message || "Could not prepare that download");
    }
  };

  /** Run a report type straight from the gallery, with no saving and no form.
   *  Reported: "Reports seems to be lacking in ease of use, and effectiveness."
   *  The builder was the only way in, so seeing "the list of AD users" meant
   *  naming a report, picking a type, ticking columns and saving it first. */
  const quickRun = async (t: TypeDef) => {
    setBusy(true);
    setMsg("");
    try {
      const columns = t.default_columns?.length
        ? t.default_columns
        : t.columns.slice(0, 6).map((c) => c.key);
      const r = await api.post(
        "/api/v1/reports-builder/preview",
        { report_type: t.key, columns, filters: [], order_by: null, row_limit: 1000 },
        scopeCfg,
      );
      // Seed the builder with what just ran, so "refine this" is one click
      // rather than starting over.
      setEditingId(null);
      setDraftName(t.label);
      setDraftDescription(t.description);
      setDraftType(t.key);
      setDraftColumns(columns);
      setDraftFilters([]);
      setDraftOrderBy(null);
      setDraftLimit(1000);
      setPreview(r.data);
      setView("preview");
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || "Could not run that report");
    } finally {
      setBusy(false);
    }
  };

  const resetDraft = () => {
    setEditingId(null);
    setDraftName("");
    setDraftDescription("");
    setDraftType("");
    setDraftColumns([]);
    setDraftFilters([]);
    setDraftOrderBy(null);
    setDraftLimit(1000);
    setPreview(null);
    setMsg("");
  };

  const startNew = () => {
    resetDraft();
    setView("edit");
  };

  const startEdit = (sr: SavedReport) => {
    // Built-in reports are read-only: "Edit" opens an editable copy (Save
    // creates a new report) so the canonical built-in stays intact.
    setEditingId(sr.built_in ? null : sr.id);
    setDraftName(sr.built_in ? `Copy of ${sr.name}` : sr.name);
    setDraftDescription(sr.description || "");
    setDraftType(sr.report_type);
    setDraftColumns(sr.columns);
    setDraftFilters(sr.filters);
    setDraftOrderBy(sr.order_by);
    setDraftLimit(sr.row_limit);
    setPreview(null);
    setMsg("");
    setView("edit");
  };








  /** Save whatever the builder hands back.
   *
   *  The draft lives in the component now, so this no longer reads a dozen
   *  pieces of page state that could drift out of step with what is on screen.
   */
  const saveFromBuilder = async (d: BuilderDraft) => {
    setBusy(true);
    setMsg("");
    try {
      const body = {
        name: d.name.trim(),
        description: d.description.trim() || null,
        report_type: d.report_type,
        columns: d.columns,
        filters: d.filters,
        order_by: d.order_by,
        row_limit: d.row_limit,
      };
      if (editingId) {
        await api.put(`/api/v1/reports-builder/${editingId}`, body);
        setMsg(`Updated "${d.name}".`);
      } else {
        const r = await api.post("/api/v1/reports-builder", body);
        setEditingId(r.data.id);
        setMsg(`Saved as "${d.name}".`);
      }
      await loadSaved();
      setView("list");
    } catch (e: any) {
      setMsg(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  /** Download a draft without saving it first. Plenty of reports are run once
   *  and never wanted again. */
  const downloadFromBuilder = async (d: BuilderDraft, fmt: "csv" | "pdf") => {
    setMsg("");
    try {
      await downloadFile(
        `/api/v1/reports-builder/download.${fmt}`,
        `${(d.name || "report").replace(/[^A-Za-z0-9._-]+/g, "_")}.${fmt}`,
        scopeCfg?.params,
        {
          method: "post",
          data: {
            report_type: d.report_type,
            columns: d.columns,
            filters: d.filters,
            order_by: d.order_by,
            row_limit: d.row_limit,
          },
        },
      );
    } catch (e: any) {
      setMsg(e?.message || "Could not prepare that download");
    }
  };

  const deleteSaved = async (sr: SavedReport) => {
    if (!window.confirm(`Delete "${sr.name}"? Schedules will be removed too.`)) return;
    await api.delete(`/api/v1/reports-builder/${sr.id}`);
    loadSaved();
  };

  const runSaved = async (sr: SavedReport) => {
    setBusy(true);
    setMsg("");
    try {
      const r = await api.post(`/api/v1/reports-builder/${sr.id}/run`, null, scopeCfg);
      setPreview(r.data);
      setEditingId(sr.id);
      setDraftType(sr.report_type);
      setDraftColumns(sr.columns);
      setView("preview");
    } catch (e: any) {
      setMsg(e.response?.data?.detail || "Run failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async (sr: SavedReport) => {
    await downloadFile(`/api/v1/reports-builder/${sr.id}/download.csv`, `${sr.name}.csv`);
  };
  const downloadPdf = async (sr: SavedReport) => {
    await downloadFile(`/api/v1/reports-builder/${sr.id}/download.pdf`, `${sr.name}.pdf`);
  };

  // ── Schedules
  const openSchedules = async (sr: SavedReport) => {
    setEditingId(sr.id);
    setSchedEditingId(null);
    setSchedDraft({
      cadence: "weekly",
      hour_utc: 13,
      day_of_week: 1,
      output_format: "csv",
      enabled: true,
      recipientsText: "",
    });
    const r = await api.get(`/api/v1/reports-builder/${sr.id}/schedules`);
    setSchedules(r.data);
    setScheduleOpen(true);
  };

  const saveSchedule = async () => {
    if (!editingId) return;
    const recipients = (schedDraft.recipientsText || "")
      .split(/[,\s;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (recipients.length === 0) {
      setMsg("At least one recipient email required.");
      return;
    }
    const body = {
      cadence: schedDraft.cadence,
      hour_utc: schedDraft.hour_utc ?? 13,
      day_of_week: schedDraft.cadence === "weekly" ? schedDraft.day_of_week ?? 1 : null,
      day_of_month: schedDraft.cadence === "monthly" ? schedDraft.day_of_month ?? 1 : null,
      recipients,
      output_format: schedDraft.output_format,
      enabled: schedDraft.enabled ?? true,
    };
    try {
      if (schedEditingId) {
        await api.put(`/api/v1/reports-builder/schedules/${schedEditingId}`, body);
      } else {
        await api.post(`/api/v1/reports-builder/${editingId}/schedules`, body);
      }
      const r = await api.get(`/api/v1/reports-builder/${editingId}/schedules`);
      setSchedules(r.data);
      setSchedEditingId(null);
      setSchedDraft({
        cadence: "weekly",
        hour_utc: 13,
        day_of_week: 1,
        output_format: "csv",
        enabled: true,
        recipientsText: "",
      });
    } catch (e: any) {
      setMsg(e.response?.data?.detail || "Schedule save failed");
    }
  };

  const editSchedule = (s: Schedule) => {
    setSchedEditingId(s.id);
    setSchedDraft({
      cadence: s.cadence,
      hour_utc: s.hour_utc,
      day_of_week: s.day_of_week,
      day_of_month: s.day_of_month,
      output_format: s.output_format,
      enabled: s.enabled,
      recipientsText: s.recipients.join(", "),
    });
  };

  const deleteSchedule = async (s: Schedule) => {
    if (!window.confirm("Delete this schedule?")) return;
    await api.delete(`/api/v1/reports-builder/schedules/${s.id}`);
    if (editingId) {
      const r = await api.get(`/api/v1/reports-builder/${editingId}/schedules`);
      setSchedules(r.data);
    }
  };

  // ── Renders

  if (view === "list") {
    return (
      <div>
        {/* "Reports Builder" named the machinery. Most visits are to run a
            standard report, not to build one. */}
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Reports</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Run a standard report in one click, or build your own from anything AeroGauge
            measures. Everything downloads as CSV or PDF and can be emailed on a schedule.
          </p>
        </div>

        {msg && (
          <div className="mb-4 text-sm rounded-lg px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300">
            {msg}
          </div>
        )}

        <ReportGallery
          types={types}
          busy={busy}
          onRun={quickRun}
          onDownload={quickDownload}
          onBuild={startNew}
          query={gallerySearch}
          onQueryChange={setGallerySearch}
          savedMatchCount={filteredSaved.length}
          documents={docReports}
          onRunDocument={runDocument}
        />

        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Saved reports
          {gallerySearch.trim() && (
            <span className="ml-2 font-normal text-gray-400 dark:text-gray-500">
              — {filteredSaved.length} of {saved.length} match “{gallerySearch.trim()}”
            </span>
          )}
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Created by
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Updated
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredSaved.map((sr) => (
                <tr key={sr.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-3 font-medium dark:text-gray-100">
                    {sr.name}
                    {sr.built_in && (
                      <span
                        className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded"
                        title="Built-in report provided by AeroGauge — always available. Edit opens a copy you can save."
                      >
                        Built-in
                      </span>
                    )}
                    {sr.description && (
                      <div className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">
                        {sr.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs font-mono">{sr.report_type}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                    {sr.created_by_username || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                    {formatDateTime(sr.updated_at)}
                  </td>
                  <td className="px-4 py-3 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => runSaved(sr)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Run
                    </button>
                    <button
                      onClick={() => downloadCsv(sr)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      CSV
                    </button>
                    <button
                      onClick={() => downloadPdf(sr)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      PDF
                    </button>
                    <button
                      onClick={() => startEdit(sr)}
                      className="text-xs text-gray-700 dark:text-gray-300 hover:underline"
                    >
                      {sr.built_in ? "Copy" : "Edit"}
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => openSchedules(sr)}
                        className="text-xs text-gray-700 dark:text-gray-300 hover:underline"
                      >
                        Schedule…
                      </button>
                    )}
                    {isAdmin && !sr.built_in && (
                      <button
                        onClick={() => deleteSaved(sr)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {saved.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                    No saved reports yet — click "+ New report" to build one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {scheduleOpen && renderScheduleModal()}
      </div>
    );
  }

  if (view === "edit") {
    return (
      <div>
        {msg && (
          <div className="mb-4 text-sm rounded-lg px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300">
            {msg}
          </div>
        )}
        <SimpleReportBuilder
          types={types}
          scopeCfg={scopeCfg}
          saving={busy}
          initial={
            draftType
              ? {
                  name: draftName,
                  description: draftDescription,
                  report_type: draftType,
                  columns: draftColumns,
                  filters: draftFilters,
                  order_by: draftOrderBy,
                  row_limit: draftLimit,
                }
              : undefined
          }
          onCancel={() => {
            setMsg("");
            setView("list");
          }}
          onSave={saveFromBuilder}
          onDownload={downloadFromBuilder}
        />
      </div>
    );
  }

  // view === "preview"
  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {draftName || "Preview"}
        </h1>
        <div className="flex items-center gap-2">
          {/* Downloading no longer requires saving the report first. */}
          <ActionButton
            onAction={() => downloadAdhoc("csv")}
            className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            pendingLabel="Preparing…"
          >
            Download CSV
          </ActionButton>
          <ActionButton
            onAction={() => downloadAdhoc("pdf")}
            className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            pendingLabel="Preparing…"
          >
            Download PDF
          </ActionButton>
          <button
            onClick={() => setView("edit")}
            className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
          >
            Refine…
          </button>
          <button
            onClick={() => {
              resetDraft();
              setView("list");
            }}
            className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
          >
            ← All reports
          </button>
        </div>
      </div>
      {draftDescription && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{draftDescription}</p>
      )}
      {msg && (
        <div className="mb-3 text-sm rounded-lg px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300">
          {msg}
        </div>
      )}
      {preview && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
            {preview.total_rows} row(s)
            {preview.capped && (
              <span className="ml-2 text-amber-700 dark:text-amber-300">
                · capped at {preview.row_limit} — download CSV for full set
              </span>
            )}
          </div>
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
              <tr>
                {preview.columns.map((c) => (
                  <th
                    key={c}
                    className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                  >
                    {/* The human label, not the raw column key. A header row of
                        sam_account_name / user_principal_name is a database
                        dump; the picker already knows what to call these. */}
                    {columnLabel(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {preview.rows.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  {preview.columns.map((c) => (
                    <td key={c} className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-300">
                      {renderCell(c, row[c]) ?? (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Inline render helper rather than a nested component — keeping it as
  // a render function (not a child component) avoids losing input focus
  // on every keystroke (React would otherwise see a fresh component
  // identity on each parent render and remount the inputs).
  function renderScheduleModal() {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recurring delivery</h2>
            <button
              onClick={() => setScheduleOpen(false)}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
            >
              &times;
            </button>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div className="space-y-2">
              {schedules.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  No schedules yet. Add one below to start recurring delivery.
                </p>
              )}
              {schedules.map((s) => (
                <div
                  key={s.id}
                  className="border dark:border-gray-700 rounded p-3 flex flex-wrap items-center gap-3 text-xs dark:text-gray-300"
                >
                  <div className="flex-1">
                    <strong>{s.cadence}</strong> at {s.hour_utc}:00 UTC
                    {s.cadence === "weekly" && ` (day ${s.day_of_week})`}
                    {s.cadence === "monthly" && ` (day ${s.day_of_month})`}
                    {" → "} <span className="font-mono">{s.recipients.join(", ")}</span> ({s.output_format})
                    {!s.enabled && (
                      <span className="ml-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">
                        disabled
                      </span>
                    )}
                    {s.last_run_at && (
                      <div className="text-gray-500 dark:text-gray-400 mt-1">
                        Last run: {formatDateTime(s.last_run_at)} ({s.last_run_status})
                        {s.last_run_error && (
                          <span className="text-red-700 dark:text-red-300"> — {s.last_run_error}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => editSchedule(s)}
                    className="text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteSchedule(s)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t dark:border-gray-700 pt-3">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {schedEditingId ? "Edit schedule" : "Add a schedule"}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Cadence</label>
                  <select
                    value={schedDraft.cadence}
                    onChange={(e) =>
                      setSchedDraft({
                        ...schedDraft,
                        cadence: e.target.value as "daily" | "weekly" | "monthly",
                      })
                    }
                    className="w-full border rounded px-2 py-1 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value="daily">daily</option>
                    <option value="weekly">weekly</option>
                    <option value="monthly">monthly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Hour (UTC)</label>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    value={schedDraft.hour_utc ?? 13}
                    onChange={(e) =>
                      setSchedDraft({ ...schedDraft, hour_utc: parseInt(e.target.value, 10) })
                    }
                    className="w-full border rounded px-2 py-1 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  />
                </div>
                {schedDraft.cadence === "weekly" && (
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Day of week (1=Mon)</label>
                    <input
                      type="number"
                      min={1}
                      max={7}
                      value={schedDraft.day_of_week ?? 1}
                      onChange={(e) =>
                        setSchedDraft({ ...schedDraft, day_of_week: parseInt(e.target.value, 10) })
                      }
                      className="w-full border rounded px-2 py-1 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                    />
                  </div>
                )}
                {schedDraft.cadence === "monthly" && (
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Day of month (1-28)</label>
                    <input
                      type="number"
                      min={1}
                      max={28}
                      value={schedDraft.day_of_month ?? 1}
                      onChange={(e) =>
                        setSchedDraft({ ...schedDraft, day_of_month: parseInt(e.target.value, 10) })
                      }
                      className="w-full border rounded px-2 py-1 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Format</label>
                  <select
                    value={schedDraft.output_format}
                    onChange={(e) =>
                      setSchedDraft({ ...schedDraft, output_format: e.target.value as "csv" | "pdf" })
                    }
                    className="w-full border rounded px-2 py-1 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value="csv">CSV</option>
                    <option value="pdf">PDF</option>
                  </select>
                </div>
              </div>
              <div className="mt-2">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Recipients (comma-separated)
                </label>
                <textarea
                  value={schedDraft.recipientsText ?? ""}
                  onChange={(e) => setSchedDraft({ ...schedDraft, recipientsText: e.target.value })}
                  rows={2}
                  className="w-full border rounded px-2 py-1 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  placeholder="ops@acme.com, reports@acme.com"
                />
              </div>
              <label className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={schedDraft.enabled ?? true}
                  onChange={(e) => setSchedDraft({ ...schedDraft, enabled: e.target.checked })}
                />
                Enabled
              </label>
              <div className="flex items-center gap-2 mt-3">
                <ActionButton onAction={() => saveSchedule()} variant="raw" className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg" pendingLabel="Saving…">
                  {schedEditingId ? "Save schedule" : "Add schedule"}
                </ActionButton>
                {schedEditingId && (
                  <button
                    onClick={() => {
                      setSchedEditingId(null);
                      setSchedDraft({
                        cadence: "weekly",
                        hour_utc: 13,
                        day_of_week: 1,
                        output_format: "csv",
                        enabled: true,
                        recipientsText: "",
                      });
                    }}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end">
            <button
              onClick={() => setScheduleOpen(false)}
              className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-1.5 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }
}
